import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  accountTermsApi,
  type AcceptTermsInternalRequest,
} from "@/lib/clients-accounts";
import { useAuth } from "@/app/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DetailModal } from "@/components/detail-modal";
import { toast } from "@/hooks/use-toast";

// ---------------------------------------------------------------------------
// Approve-terms-internally dialog
//
// Wraps POST /api/accounts/{id}/terms/accept — records acceptance on the
// customer's behalf (e.g. verbal/paper acceptance). AcceptTermsInternalRequest
// accepts `termsId`, `acceptedByName`, and an optional `signatureText`.
// ---------------------------------------------------------------------------

export function ApproveTermsDialog({
  open,
  onOpenChange,
  accountId,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  accountId: number;
}) {
  const qc = useQueryClient();
  const { me } = useAuth();
  const [termsId, setTermsId] = useState<string>("");
  const [acceptedByName, setAcceptedByName] = useState(me?.displayName ?? "");
  const [signatureText, setSignatureText] = useState("");

  const { data: termsList } = useQuery({
    queryKey: ["account-terms-list"],
    queryFn: () => accountTermsApi.list(),
    staleTime: 60_000,
    enabled: open,
  });

  function resetForm() {
    setTermsId("");
    setAcceptedByName(me?.displayName ?? "");
    setSignatureText("");
  }

  const acceptMut = useMutation({
    mutationFn: (body: AcceptTermsInternalRequest) => accountTermsApi.internalAccept(accountId, body),
    onSuccess: () => {
      toast.success("Terms accepted");
      void qc.invalidateQueries({ queryKey: ["account", accountId] });
      onOpenChange(false);
      resetForm();
    },
    onError: () => toast.error("Failed to accept terms"),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!termsId || !acceptedByName) return;
    acceptMut.mutate({
      termsId: Number(termsId),
      acceptedByName,
      signatureText: signatureText || null,
    });
  }

  return (
    <DetailModal open={open} onOpenChange={onOpenChange} title="Approve terms">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="at-terms">Terms version *</Label>
          <Select value={termsId} onValueChange={setTermsId}>
            <SelectTrigger id="at-terms">
              <SelectValue placeholder="Select terms version" />
            </SelectTrigger>
            <SelectContent>
              {(termsList?.items ?? []).map((t) => (
                <SelectItem key={t.termsId} value={String(t.termsId)}>
                  v{t.versionNumber} — {t.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="at-accepted-by">Accepted by *</Label>
          <Input
            id="at-accepted-by"
            value={acceptedByName}
            onChange={(e) => setAcceptedByName(e.target.value)}
            required
          />
        </div>
        <div>
          <Label htmlFor="at-signature">Signature</Label>
          <Input
            id="at-signature"
            value={signatureText}
            onChange={(e) => setSignatureText(e.target.value)}
          />
        </div>
        <Button type="submit" disabled={acceptMut.isPending || !termsId || !acceptedByName}>
          {acceptMut.isPending ? "Approving…" : "Approve"}
        </Button>
      </form>
    </DetailModal>
  );
}
